import "@/styles/nav-extras.css";
import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Icon } from "../ui/Icon";
import { useAuth } from "@/state/AuthContext";
// 余额低阈值 = 一次体检的价格,从价目常量取,避免硬编码数字随调价漂移。
import { COSTS } from "@/lib/costs";
import { resolveBillingCoverage } from "@/lib/billingAffordability";

// Workbench owns task selection; records and account are separate utilities.
type Leaf = { to: string; label: string; end?: boolean };
const NAV: Leaf[] = [
  { to: "/app", label: "工作台", end: true },
  { to: "/app/history", label: "我的记录" },
];

export function TopNav() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const [isDark, setIsDark] = useState(() => {
    const explicit = document.documentElement.getAttribute("data-theme");
    if (explicit === "dark") return true;
    if (explicit === "light") return false;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  });
  function toggleTheme() {
    const next = isDark ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", next);
    try {
      localStorage.setItem("rh-theme", next);
    } catch {
      /* 隐私模式下 localStorage 可能抛错,忽略 */
    }
    setIsDark(!isDark);
  }

  // 点击外部关闭用户菜单。
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  async function handleLogout() {
    setMenuOpen(false);
    await auth.logout();
    navigate("/auth", { replace: true });
  }

  const user = auth.user;
  const checkupCoverage = user
    ? resolveBillingCoverage({
        cost: COSTS.checkup,
        creditTotal: user.credits.total,
      })
    : null;

  return (
    <header className="topnav">
      <NavLink to="/app" className="brand-mark">
        <span className="research-monogram" aria-hidden="true">研</span>
        研鉴
      </NavLink>
      <nav className="topnav-links" aria-label="主导航">
        {NAV.map((e) => (
          <NavLink key={e.to} to={e.to} end={e.end} className={({ isActive }) => (isActive ? "active" : "")}>
            {e.label}
          </NavLink>
        ))}
      </nav>
      <div className="topnav-actions">
        <button
          type="button"
          className="theme-toggle"
          onClick={toggleTheme}
          title={isDark ? "切换到浅色" : "切换到深色"}
          aria-label="切换深色 / 浅色主题"
          style={{
            background: "none",
            border: "1px solid var(--border)",
            borderRadius: "var(--r-full)",
            width: 34,
            height: 34,
            minWidth: 34,
            cursor: "pointer",
            color: "var(--ink-2)",
            fontSize: 15,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span aria-hidden="true">{isDark ? "☀" : "☾"}</span>
        </button>
        {user && (
          <>
            <NavLink
              to="/app/account"
              className={`credit-pill${checkupCoverage && !checkupCoverage.sufficient ? " is-low" : ""}`}
              title={
                `积分余额 ${user.credits.total}${
                  user.credits.daily > 0 ? `,其中 ${user.credits.daily} 今日 24:00 到期` : ""
                }${user.credits.bundle > 0 ? `,套餐积分 ${user.credits.bundle}` : ""};点击查看明细`
              }
            >
              <span aria-hidden="true">◈</span>
              <span className="mono">{user.credits.total}</span>
              <span className="credit-pill-pack">积分</span>
            </NavLink>
            <div className="user-menu-wrap" ref={menuRef}>
              <button
                type="button"
                className="user-menu-btn"
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <Icon name="user" size={15} />
                <span className="user-menu-name">{user.username}</span>
                <Icon name="chevron-down" size={13} />
              </button>
              {menuOpen && (
                <div className="user-menu" role="menu">
                  <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); navigate("/app/account"); }}>
                    <Icon name="user" size={14} /> 账户与积分
                  </button>
                  <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); navigate("/app/settings"); }}>
                    <Icon name="sliders" size={14} /> 个人设置
                  </button>
                  <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); navigate("/app/guide"); }}>
                    <Icon name="book" size={14} /> 使用说明
                  </button>
                  <button type="button" role="menuitem" className="danger" onClick={handleLogout}>
                    <Icon name="x" size={14} /> 退出登录
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </header>
  );
}
